import {
  CASE_TYPE_SCHEMA,
  emptyValueForSchema,
  generateDefaultWeather,
  getSchemaAt,
  WEATHER_CONDITIONS,
} from "../../../../case-spec";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { WEATHER_CONDITION_LABELS } from "@/lib/phone-json-labels";
import {
  ArrayShell,
  EnumSelect,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  enumOptionsAt,
  isRecord,
  num,
  str,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

export function WorldPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const [tab, setTab] = useState<"locations" | "weather">("locations");
  const path =
    tab === "locations"
      ? "canon.world.locations"
      : "canon.world.weather";
  const items = asArray(getAtPath(root, path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(items.length);
  const weatherSeed =
    str(getAtPath(root, "id")) ||
    str(getAtPath(root, "meta.title")) ||
    "peekie-weather";

  const generateWeather = () => {
    onChangeRoot(setAtPath(root, "canon.world.weather", generateDefaultWeather(weatherSeed)));
    setOpenIndex(0);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        <Button
          type="button"
          size="sm"
          variant={tab === "locations" ? "secondary" : "ghost"}
          onClick={() => {
            setTab("locations");
            setOpenIndex(null);
          }}
        >
          地点 (
          {asArray(getAtPath(root, "canon.world.locations")).length})
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === "weather" ? "secondary" : "ghost"}
          onClick={() => {
            setTab("weather");
            setOpenIndex(null);
          }}
        >
          天气 (
          {asArray(getAtPath(root, "canon.world.weather")).length})
        </Button>
      </div>

      {tab === "weather" && items.length === 0 && !disabled ? (
        <Button type="button" variant="outline" onClick={generateWeather}>
          生成默认天气
        </Button>
      ) : null}

      <ArrayShell
        title={tab === "locations" ? "地点" : "天气"}
        count={items.length}
        disabled={disabled}
        emptyText={
          tab === "weather"
            ? "尚未配置天气。手机端会按案件 ID 自动生成稳定的默认天气。"
            : undefined
        }
        onAdd={() => {
          const schema = getSchemaAt(CASE_TYPE_SCHEMA, `${path}[0]`);
          const row = schema
            ? emptyValueForSchema(schema)
            : tab === "locations"
              ? { name: "", lat: 0, lng: 0 }
              : {
                  day: "",
                  city: "",
                  temp_high_c: 0,
                  temp_low_c: 0,
                  condition: WEATHER_CONDITIONS[0],
                  precip_mm: 0,
                };
          onChangeRoot(appendAtPath(root, path, row));
          setOpenIndex(items.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {items.map((item, index) => {
            const rec = isRecord(item) ? item : {};
            const itemPath = childPath(path, index);
            const condition = str(rec.condition);
            const weatherTitle = [
              str(rec.city),
              str(rec.day),
              condition
                ? (WEATHER_CONDITION_LABELS[condition] ?? condition)
                : "",
            ]
              .filter(Boolean)
              .join(" · ");
            const title =
              tab === "locations"
                ? str(rec.name) || `地点 ${index + 1}`
                : weatherTitle || `天气 ${index + 1}`;
            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={items.length}
                arrayPath={path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                {tab === "locations" ? (
                  <>
                    <TextField
                      label="名称"
                      value={str(rec.name)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "name"), v),
                        )
                      }
                    />
                    <TextField
                      label="纬度"
                      value={String(num(rec.lat))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "lat"),
                            Number(v) || 0,
                          ),
                        )
                      }
                    />
                    <TextField
                      label="经度"
                      value={String(num(rec.lng))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "lng"),
                            Number(v) || 0,
                          ),
                        )
                      }
                    />
                    <TextField
                      label="地址"
                      value={str(rec.address)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "address"), v),
                        )
                      }
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label="日期"
                      fieldKey="day"
                      value={str(rec.day)}
                      disabled={disabled}
                      placeholder="YYYY-MM-DD"
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "day"), v),
                        )
                      }
                    />
                    <TextField
                      label="城市"
                      value={str(rec.city)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "city"), v),
                        )
                      }
                    />
                    <TextField
                      label="最高温 ℃"
                      value={String(num(rec.temp_high_c))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "temp_high_c"),
                            Number(v) || 0,
                          ),
                        )
                      }
                    />
                    <TextField
                      label="最低温 ℃"
                      value={String(num(rec.temp_low_c))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "temp_low_c"),
                            Number(v) || 0,
                          ),
                        )
                      }
                    />
                    <EnumSelect
                      label="天气状况"
                      fieldKey="condition"
                      value={str(rec.condition)}
                      options={enumOptionsAt(
                        "canon.world.weather[0].condition",
                      )}
                      optionLabels={WEATHER_CONDITION_LABELS}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "condition"),
                            v,
                          ),
                        )
                      }
                    />
                    <TextField
                      label="降水量 mm"
                      value={String(num(rec.precip_mm))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "precip_mm"),
                            Number(v) || 0,
                          ),
                        )
                      }
                    />
                  </>
                )}
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}
